import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AIConversationService } from '../../../../../core/services/ai-conversation.service';
import { AIContextService } from '../../../../../core/services/ai-context.service';
import { FeatureFlagsViewerService } from '../../../../../core/services/feature-flags-viewer.service';
import { AskPanelHostService } from '../../../../../shared/ask/ask-panel-host';
import { captureMorph, isReducedMotion } from '../../../../../core/motion';
import { AnalyticsService } from '../../../../../core/services/analytics.service';
import { ANALYTICS_EVENTS } from '../../../../../core/analytics/events';

/**
 * A phone: the palette's narrow breakpoint (`--bp-narrow`, 768px), where the
 * preview column, and with it the Ask pane, is not drawn.
 */
export function isPhoneWidth(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(max-width: 768px)').matches;
}

/**
 * RESPONSIBILITY: the palette's own Ask conversation — starting it, tracking
 * it, and handing it over to the docked panel when the user escalates.
 *
 * SIGNALS:
 *   active     — an ask has been made in this palette session
 *   query      — the question that started it
 *   messages / streaming / progress / answer / quickReplies / widgets / error
 *   hasAnswer  — the pane has something to show
 *
 * USED BY: CmdkComponent and CmdkAskPaneComponent, both of which resolve the
 *   COMPONENT-scoped instance (see below).
 *
 * BACKEND CONTRACT: none of its own. Everything goes through
 *   `AIConversationService` → gemba-ai `POST /api/conversation/{id}?stream=1`.
 *
 * AI CONTEXT — why this is provided on the component, not in root
 *
 * `AIConversationService` holds ONE conversation: one message list, one
 * `conversationId`, one `abortController`, and an `isStreaming` flag that acts
 * as a mutual-exclusion lock. Today the legacy palette copes by calling
 * `clearConversation()` before every ask, which destroys whatever the docked
 * panel was showing; without that clear, the lock silently REFUSES the ask
 * while the panel is streaming.
 *
 * So `CmdkComponent` provides its own instance of both services. The palette
 * gets a genuinely separate conversation — separate history, separate
 * `conversationId`, separate abort handle — and the panel is untouched.
 *
 * Nothing is needed from gemba-ai for this. Conversation ids are server-issued
 * per `POST /api/conversation` with no per-user uniqueness, history is
 * client-supplied on every request, and the agent loop is stateless — two live
 * conversations for one user already work. The one hard rule is that the two
 * threads must NOT share an id: the server derives each message's ordinal from
 * the client's own history length, so a shared id interleaves ordinals and
 * corrupts the stored transcript. Separate instances give separate ids for
 * free, because each lazily creates its own on first send.
 */
@Injectable()
export class CmdkAskService {
  /** The palette's OWN conversation. Component-provided — see the class docs. */
  private readonly conversation = inject(AIConversationService);
  /** The panel's (root) conversation: what a recent thread continues, and where an answer goes on. */
  private readonly panel = inject(AIConversationService, { skipSelf: true });
  private readonly router = inject(Router);
  private readonly context = inject(AIContextService);
  private readonly featureFlags = inject(FeatureFlagsViewerService);
  private readonly hosts = inject(AskPanelHostService);
  private readonly analytics = inject(AnalyticsService);

  private readonly _query = signal('');
  private readonly _active = signal(false);
  private askStartedAt = 0;
  private answeredFor = '';
  /**
   * "New chat" was chosen: until the palette closes (`end`), asks here go
   * without the panel's recent thread, even after Esc drops an answer.
   */
  private fresh = false;

  readonly query = this._query.asReadonly();
  readonly active = this._active.asReadonly();

  readonly messages = this.conversation.messages;
  readonly streaming = this.conversation.isStreaming;
  readonly error = this.conversation.error;

  /** True when the tenant has AI at all. Gates the row, the pane and ⌘↵. */
  readonly enabled = this.featureFlags.isEnabled('ai');

  /** The most recent assistant turn, or undefined. */
  readonly latest = computed(() => {
    const assistant = this.messages().filter(m => m.role === 'assistant');
    return assistant[assistant.length - 1];
  });

  /** Progress line while the agent works — the gemba `info` events. */
  readonly progress = computed(() => this.latest()?.progressMessage ?? null);

  /** The answer prose. Empty while only progress has arrived. */
  readonly answer = computed(() => this.latest()?.content ?? '');

  /**
   * The answer's structured widgets, if any — an availability list, a booking
   * receipt, an issue flow, a tutorial. The pane draws them with the shared
   * `im-ai-widget-outlet`, the same renderer the docked panel and `/ai` use.
   */
  readonly widgets = computed(() => this.latest()?.widgets ?? []);

  /** Client-derived chips. Present only when there is no structured widget. */
  readonly quickReplies = computed(() => this.latest()?.quickReplies ?? []);

  /**
   * The pane has something to show.
   *
   * `_active` alone is the condition once an ask has been made — NOT the
   * presence of prose. gemba can return a successful turn whose `response` is
   * an empty string; keying on `answer().length` made that case render an
   * empty pane AND disable "Continue in chat", so the user could neither see
   * what happened nor escalate to a surface that might tell them.
   */
  readonly hasAnswer = computed(() => this._active());

  /** A turn has settled and produced no prose. Rendered, not swallowed. */
  readonly answeredEmpty = computed(() =>
    this._active() && !this.streaming() && this.answer().length === 0
    && this.progress() === null && this.error() === null,
  );

  /** The building / floor the answer is grounded in, for the pane's header. */
  readonly contextLabel = computed(() => this.context.contextLabel());

  /**
   * Ask a question.
   *
   * Unlike the legacy palette this does NOT clear anything first: the palette
   * owns its own conversation, so a follow-up genuinely continues it and the
   * docked panel is never touched.
   */
  ask(query: string, trigger: 'row' | 'shortcut' | 'zero-results' | 'voice' | 'new-chat', resultCount = 0): void {
    const text = query.trim();
    // Defence in depth. The row is not rendered and ⌘↵ is inert when AI is
    // off, but a deep link or a spoken hand-off could still reach here.
    if (!text || !this.featureFlags.is('ai')) return;
    if (this.conversation.isStreaming()) return;
    // Ruling 16: Ask within about 30 minutes of the last message continues
    // that conversation. The palette takes the panel's recent thread as its
    // context (never its id), so the answer knows what was said, the pane
    // shows it, and "Continue in panel" hands the whole thread back.
    // "New chat" in the pane opts out until the palette closes.
    if (!this.fresh && this.conversation.messages().length === 0 && this.panel.isRecent()) {
      this.conversation.continueFrom(this.panel);
    }

    this._query.set(text);
    this._active.set(true);
    this.askStartedAt = performance.now();
    this.answeredFor = '';

    this.analytics.capture(ANALYTICS_EVENTS.CMDK_ASK_STARTED, {
      query_length: text.length,
      result_count_at_ask: resultCount,
      scope: 'all',
      trigger,
    });
    this.analytics.capture(ANALYTICS_EVENTS.AI_MESSAGE_SENT, {
      source: 'command-palette',
      chars: text.length,
    });

    void this.conversation.sendMessage(text);
  }

  /** A follow-up from a quick-reply chip or the composer. */
  followUp(text: string): void {
    const trimmed = text.trim();
    if (!trimmed || this.conversation.isStreaming()) return;
    this.answeredFor = '';
    void this.conversation.sendMessage(trimmed);
  }

  /** Stop generating. `cancelStream()` had zero callers before this. */
  stop(): void {
    this.conversation.cancelStream();
  }

  /**
   * Fire the answered event exactly once per turn. Called by the pane when a
   * turn settles — a `computed` cannot do this, because reporting is a side
   * effect and must not run on every read.
   */
  reportAnswered(): void {
    const answer = this.answer();
    if (!answer || this.streaming()) return;
    if (this.answeredFor === answer) return;
    this.answeredFor = answer;

    this.analytics.capture(ANALYTICS_EVENTS.CMDK_ASK_ANSWERED, {
      latency_ms: Math.round(performance.now() - this.askStartedAt),
      had_options: this.widgets().length > 0,
      // Provenance has no backing data anywhere in the contract yet — the
      // gemba `message` event carries only `{response, options}`. Reported
      // honestly as false rather than omitted, so the series is comparable
      // once citations do land.
      had_citations: false,
      chars: answer.length,
    });
  }

  /**
   * Hand the conversation to the Ask panel and leave: the current workspace's
   * (`AskPanelHostService`), else the shell's docked panel.
   *
   * The panel's instance ADOPTS this one's thread, so it opens showing the
   * same exchange instead of a blank composer. That is the whole reason the
   * palette runs a separate conversation rather than sharing one.
   */
  handOffToPanel(panelConversation: AIConversationService): void {
    if (!this.hasAnswer()) return;
    // Never hand over mid-stream. `adoptFrom` copies the message array BY
    // VALUE, and this method then aborts the source's fetch — so the panel
    // would inherit a bubble frozen at "Thinking" (or mid-sentence) with
    // `isStreaming` false, which nothing will ever resolve. The tokens still
    // in flight get written into the released instance and discarded.
    //
    // Guarded here rather than at the two call sites (the pane's button and
    // the ⌘⇧↵ intent) so neither can bypass it, and so the escalation
    // analytics event does not fire for a hand-off that will not happen.
    if (this.streaming()) return;
    this.analytics.capture(ANALYTICS_EVENTS.CMDK_ASK_ESCALATED, { to: 'panel' });
    // The panel grows out of the answer it continues (the shell's docked
    // panel does; a workspace host opens its own way). Captured now, because
    // the palette closes as the panel opens.
    const pane = typeof document !== 'undefined' ? document.querySelector('[data-testid="cmdk-ask-pane"]') : null;
    const origin = pane && !isReducedMotion() ? captureMorph(pane) : null;
    panelConversation.adoptFrom(this.conversation);
    this.reset();
    // Ruling 17: a phone has no panel beside the page, so the thread goes on
    // full screen at /ai.
    if (isPhoneWidth()) {
      void this.router.navigateByUrl('/ai');
      return;
    }
    // The current workspace's Ask panel (Home's), else the shell's docked one.
    this.hosts.open(origin);
  }

  /**
   * Ask on a phone (ruling 17). The palette there has no preview column, so an
   * answer in it would never be seen: the question goes to the panel's
   * conversation, which continues if it is recent (ruling 16), and on at /ai.
   */
  askOnPage(query: string, trigger: 'row' | 'shortcut' | 'zero-results' | 'voice', resultCount = 0): boolean {
    const text = query.trim();
    if (!text || !this.featureFlags.is('ai') || this.panel.isStreaming()) return false;
    this.analytics.capture(ANALYTICS_EVENTS.CMDK_ASK_STARTED, {
      query_length: text.length, result_count_at_ask: resultCount, scope: 'all', trigger,
    });
    this.analytics.capture(ANALYTICS_EVENTS.CMDK_ASK_ESCALATED, { to: 'page' });
    this.panel.startNewSession();
    void this.panel.sendMessage(text);
    void this.router.navigateByUrl('/ai');
    return true;
  }

  /**
   * "New chat" in the pane (ruling 16: always one tap away): the same question
   * again, on a conversation of its own, without the panel's recent thread.
   */
  newChat(): void {
    const query = this._query();
    this.reset();
    this.fresh = true;
    if (query) this.ask(query, 'new-chat');
  }

  /** Drop the answer, keeping the query. The Esc ladder's `dismiss-answer`. */
  reset(): void {
    this.conversation.cancelStream();
    this.conversation.clearConversation();
    this._active.set(false);
    this._query.set('');
    this.answeredFor = '';
  }

  /**
   * The palette closed. It stays mounted in the shell, so this service outlives
   * the session: drop the answer, and let the next session continue a recent
   * thread again (ruling 16) whatever New chat said in this one.
   */
  end(): void {
    this.reset();
    this.fresh = false;
  }
}
